/**
 * The words beside Advanced targeting on the wizard's audience step (PR 4). Every sentence
 * about what X does quotes the guide or the reference saved in `audits/2026-10-23/x-docs/`
 * (G:/R: by line); every sentence about this console says it as this console's.
 */
import type { KeywordType, PrimaryProblem } from "./primary-targeting.ts";

export const ADVANCED_HEADING = "Advanced targeting — widens the audience";

/** 🔴 The one sentence this block cannot lose: these three add people, they do not filter. */
export const WIDENS =
	"Interests, keywords and follower look-alikes ADD people rather than filter them. X: «“Primary” Targeting Types will get ∪‘d (i.e. put in a logical union). Other Targeting Types will get AND‘d.» So a country narrows the audience, and each interest, keyword or look-alike here widens it — within the countries, languages and devices above.";

export const INTEREST_SEARCH_LABEL = "Interest";
export const INTEREST_PLACEHOLDER = "Type to search X's interests";
export const INTERESTS_CHOSEN = "Interests:";
export const INTERESTS_NOT_READ = "X's interests could not be read.";
export const NO_INTEREST_MATCH = "X matched no interest for that.";
export const ADD_INTEREST = "Add";

export const KEYWORD_LABEL = "Keyword";
export const KEYWORD_TYPE_LABEL = "Match";
export const KEYWORDS_CHOSEN = "Keywords:";
export const ADD_KEYWORD = "Add keyword";

/** The match types in the guide's words (G:554, G:556, G:557). */
export const KEYWORD_TYPE_NAMES: Readonly<Record<KeywordType, string>> = {
	BROAD_KEYWORD:
		"Broad — X: «match all words, independent of order… Will automatically be expanded when possible»",
	PHRASE_KEYWORD: "Phrase — X: «match the exact keywords string, other keywords can be present»",
	EXACT_KEYWORD: "Exact — X: «match exactly the keywords string, not any others»",
};

export const KEYWORD_HELP =
	"Under Broad, X expands «car repair» to «automobile fix»; to keep a word as typed, put + before it — X: «like “+boat +jet”». An exact keyword applies in search results only: X lists «Non-exact Keyword Types» for timelines and profiles.";

/** A chosen keyword's label, by its match type. */
export function keywordLabel(k: { type: KeywordType; text: string }): string {
	const kind =
		k.type === "BROAD_KEYWORD" ? "broad" : k.type === "PHRASE_KEYWORD" ? "phrase" : "exact";
	return `keyword «${k.text}» (${kind})`;
}

export const LOOKALIKE_LABEL = "Follower look-alikes: X user id";
export const LOOKALIKES_CHOSEN = "Look-alikes of:";
export const ADD_LOOKALIKE = "Add user id";

/**
 * 🔴 Why the field takes an id and not an @handle — said, so nobody types a handle and
 * wonders. The Ads reference has no lookup from a handle to an id; this console offers none.
 */
export const LOOKALIKE_HELP =
	"X: «Target people with the same interests as followers of specific users.» It takes the account's numeric X user id, not its @handle — the X Ads reference has no lookup from a handle to an id, so this console cannot turn one into the other.";

/** Where each applies, from the guide's targeting-by-placement list (G:483–485). */
export const PLACEMENT_NOTE =
	"On search results X lists «Keyword Types (All)» and no interests or look-alikes; on timelines and profiles it lists interests, look-alikes and non-exact keywords.";

export function limitsNote(interests: number, keywords: number, lookalikes: number): string {
	return `X's limits per ad group: up to ${interests} interests, ${keywords} keywords and ${lookalikes} users for look-alikes.`;
}

export const PRIMARY_PROBLEMS: Readonly<Record<PrimaryProblem, string>> = {
	"keyword-blank": "Type a keyword first.",
	"keyword-padded":
		"The keyword starts or ends with a space — remove it, so X saves the keyword as you mean it.",
	"keyword-twice": "That keyword is already in the list with this match type.",
	"lookalike-id":
		"An X user id is digits only — not an @handle, not a name. This console has no lookup from one to the other.",
};
